class FumLineHighlight {
    constructor(
        selectorName = '',
        cssVariablePrefix = '',
        ignoreSX = false,
        disableAnimation = false,
        transitionProp = {
            duration: null,
            timing: null
        },
        svgPath = null,
        attributes = {
            style: {
                sx: 'style',
                color: 'color',
                thickness: 'thickness',
                transitionDuration: 'transition-duration',
                transitionTiming: 'transition-timing',
                opacity: 'opacity'
            },
            animate: 'animate'
        }
    ) {
        // Global Configuration
        this.globalConfig = {
            selectorName,
            cssVariablePrefix,
            els: document.querySelectorAll(selectorName),
            svg: {
                namespace: 'http://www.w3.org/2000/svg',
                transformVars: {
                    widthGain: 1,
                    heightGain: 1
                }
            },
            ignoreSX,
            disableAnimation,
            attributes,
            svgPath: svgPath ?? this.getDefaultSvgPath()
        }

        // Styles
        this.styles = {
            thickness: "2px",
            color: '#000',
            width: '100%',
            height: '100%'
        }
        // Global CSS Properties
        this.stylesheetProperties = [
            {
                name: `${this.globalConfig.selectorName}`,
                styles: [
                    {
                        position: 'relative',
                        left: '0em',
                        textDecoration: 'none',
                        display: 'inline-block', // Ensure it applies to the text properly
                        color: '#000'
                    }
                ]
            },
            {
                name: `${this.globalConfig.selectorName} svg`,
                styles: [
                    {
                        position: 'absolute',
                        top: '0',
                        right: '0',
                        left: '0',
                        zIndex: '-10'
                    }
                ]
            },
            {
                name: `${this.globalConfig.selectorName}[animate=\"true\"] path`,
                styles: [
                    {
                        strokeLinecap: 'round'
                    }
                ]
            },
            {
                name: `${this.globalConfig.selectorName}[animate=\"false\"] path`,
                styles: [
                    {
                        strokeLinecap: 'round',
                        strokeDashoffset: '0'
                    }
                ]
            },
            {
                name: `${this.globalConfig.selectorName} path`,
                styles: [
                    {
                        strokeLinecap: 'round'
                    }
                ]
            },
            {
                name: `@keyframes ${this.globalConfig.selectorName}-svg-path--animation`,
                styles: [
                    {
                        from: { transform: 'scaleX(0)' }, // Start from zero width
                        to: { transform: `scaleX(1)` }   // End at full width
                    }
                ]
            }
        ];

        // CSS Classes
        this.cssClassStr = '';

        // Global Transition Properties
        this.transitionProperties = {
            duration: transitionProp.duration ?? '0.5s',
            timing: transitionProp.timing ?? 'ease-in-out'
        };

        // Observer 
        this._observer = {
            enabled: false,
            options: {
                root: null,
                rootMargin: '0px',
                threshold: 0.1
            },
            callback: this._observerCallback.bind(this)
        }

        return this;
    }

    sx(sxParams) {
        this.styles = sxParams;

        // Function to recursively apply styles
        const applyStyles = (baseSelector, stylesObject) => {
            Object.entries(stylesObject).forEach(([key, value]) => {
                if (typeof value === 'object' && !Array.isArray(value)) {
                    // Handle nested objects, e.g., _anyname
                    applyStyles(`${baseSelector} ${key.replace('_', '')}`, value);
                } else {
                    // Apply styles to the base selector
                    this.stylesheetProperties.forEach(property => {
                        if (property.name.includes(baseSelector)) {
                            property.styles.forEach(style => {
                                style[key] = value;
                            });
                        }
                    });
                }
            });
        };

        // Iterate over each key in sxParams
        Object.keys(sxParams).forEach(key => {
            if (key === 'root' && typeof sxParams[key] === 'object') {
                // Apply styles starting from the root selector
                applyStyles(this.globalConfig.selectorName, sxParams[key]);
            }
        });
        
        return this;
    }

    generateStylesheet() {
        let cssString = '';

        if (this.stylesheetProperties != null) {
            this.stylesheetProperties.forEach(property => {
                const _iterateStyles = (prop) => {
                    let _ret = '';
                    prop.forEach(style => {
                        Object.entries(style).forEach(([key, value]) => {
                            // Convert camelCase to kebab-case for CSS properties
                            const cssProperty = key.replace(/([A-Z])/g, "-$1").toLowerCase();
                            _ret += `   ${cssProperty}: ${value};\n`;
                        });
                    });
                    return _ret;
                }
                
                // Handle @keyframes separately
                if (property.name.startsWith('@keyframes')) {
                    cssString += `${property.name} {\n`;

                    property.styles.forEach(keyframe => {
                        Object.entries(keyframe).forEach(([key, value]) => {
                            cssString += `  ${key} {\n`;

                            // Process the styles inside each keyframe
                            Object.entries(value).forEach(([k, v]) => {
                                const cssProperty = k.replace(/([A-Z])/g, "-$1").toLowerCase();
                                cssString += `    ${cssProperty}: ${v};\n`;
                            });

                            cssString += `  }\n`;
                        });
                    });

                    cssString += `}\n`;
                } else {
                    cssString += `${property.name} {\n`;
                    cssString += _iterateStyles(property.styles);
                    cssString += `}\n`;
                }
            })
        }

        if (this.cssClassStr != null) {
            cssString += this.cssClassStr;
        }

        console.log(cssString);

        // Create a new style element
        const style = document.createElement('style');

        // Add the CSS rules to the style element
        if (style.styleSheet) {
            // For IE
            style.styleSheet.cssText = cssString;
        } else {
            // For other browsers
            style.appendChild(document.createTextNode(cssString));
        }

        // Append the style element to the head of the document
        document.head.appendChild(style);
    }

    getDefaultSvgPath() {
        return 'M10,20 L155,20 L165,0 L0,5 Z';
    }

    setPropertyVars() {
        this.globalConfig.els.forEach((element) => {
            Object.entries(this.globalConfig.attributes.style)
            .forEach(([key, value]) => {
                try {
                    const getAttributeValue = element.getAttribute(value);
                    element.style.setProperty(`${this.globalConfig.cssVariablePrefix}-style--${value ?? key}`, getAttributeValue);
                } catch (e) {}
            });
        })
    }

    _randStr(length = 32, toLower = true) {
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let result = '';
        for (let i = 0; i < length; i++) {
            const randomIndex = Math.floor(Math.random() * characters.length);
            result += characters[randomIndex];
        }
        return toLower ? result.toLowerCase() : result;
    }

    parseSXPerElement() {
        this.globalConfig.els.forEach((element) => {
            const _style = element.getAttribute(this.globalConfig.attributes.style.sx);
            if (_style != null) {
                const _className = 'fum-' + this._randStr(10);
                this.cssClassStr += `.${_className} { ${_style} }`;
                element.classList.add(_className);
                element.removeAttribute(this.globalConfig.attributes.style.sx);
            }
        });
    }

    _circleShape(element, state, delay = 1000) {
        const _vars = {
            width: element.offsetWidth,
            height: element.offsetHeight,
            el: {
                animate: element.getAttribute(this.globalConfig.attributes.animate)?.toLowerCase() == "true" ? true : false
            },
            pathStyleVars: {
                thickness: element.getAttribute(this.globalConfig.attributes.style.thickness) ?? this.styles?.thickness ?? "2px",
                color: element.getAttribute(this.globalConfig.attributes.style.color) ?? this.styles?.color ?? "#000",
                opacity: element.getAttribute(this.globalConfig.attributes.style.opacity) ?? this?.styles?.opacity ?? 1
            },
            transitionVars: {
                duration: element.getAttribute(this.globalConfig.attributes.style.transitionDuration) ?? this.transitionProperties.duration ?? '1s',
                timing: element.getAttribute(this.globalConfig.attributes.style.transitionTiming) ?? this.transitionProperties.timing ?? 'ease-in-out',
                keyframeName: `${this.globalConfig.selectorName}-svg-path--animation`
            },
            scaleX: 1.04,
            // scaleY: 1 * (element.offsetHeight * 0.008),
            // top: `${element.offsetHeight * 0.005}em`
            scaleY: 1,
            top: `40%`,
            left: `0`
        }

        // Create SVG element
        let _svg = document.createElementNS(this.globalConfig.svg.namespace, "svg");

        // Set styles
        _svg.style.width =  _vars.width;
        _svg.style.height =  _vars.height;
        _svg.style.transform = `scale(${_vars.scaleX}, ${_vars.scaleY})`;
        _svg.style.top = _vars.top;
        _svg.style.left = _vars.left;

        // Set attributes
        _svg.setAttribute("width", this.styles?.width ?? _vars.width);
        _svg.setAttribute("height", this.styles?.height ?? _vars.height);
        // _svg.setAttribute("viewBox", `0 0 ${_vars.width} ${_vars.height}`);
        _svg.setAttribute("viewBox", `0 0 165 22`);

        // Append '_svg' as child on 'element'
        element.appendChild(_svg);

        // Create path element
        const _path = document.createElementNS(this.globalConfig.svg.namespace, "path");
        _path.setAttribute("d", this.globalConfig.svgPath);

        // Append '_path' as child on '_svg'.
        _svg.appendChild(_path);
        
        const _setVisibility = (state = false) => {
            _path.setAttribute("d", this.globalConfig.svgPath ?? circlePath(-0.15, 0.05, 150, 190, 0.05, 0.3));
            _path.style.transform = `scaleX(0)`;

            // Force a reflow to ensure the above settings are applied before animation
            _path.getBoundingClientRect();

            if (state) {
                _path.setAttribute("visibility", "visible");
                _path.style.animation = `${_vars.transitionVars.keyframeName} ${_vars.transitionVars.duration} ${_vars.transitionVars.timing} forwards`;
            } else {
                _path.setAttribute("visibility", "hidden");
                _path.style.transform = `scaleX(0)`;
            }

        }

        // count length
        _path.setAttribute("pathLength", 100000);

        _setVisibility(false);

        setTimeout(() => {
            _setVisibility(state);
        }, delay);
        
    }

    // Observer
    useObserver(obParams, enabled = true) {
        this._observer.enabled = enabled;
        this._observer.options.root = obParams?.root ?? null;
        this._observer.options.rootMargin = obParams?.rootMargin ?? this._observer.options?.rootMargin;
        this._observer.options.threshold = obParams?.threshold ?? this._observer.options?.threshold;

        return this;
    }

    // Observer callback
    _observerCallback(entries, observer) {
        entries.forEach((entry) => {
            const _element = entry.target;
            // console.log(entry);
            this._circleShape(_element, entry.isIntersecting, 500);
        })
    }

    render() {
        this.parseSXPerElement();
        this.globalConfig.els.forEach((element) => {
            if (this._observer.enabled) {
                const observer = new IntersectionObserver(this._observer.callback, this._observer.options);
                observer.observe(element);
            } else {

            }
        });
    }

    init(enableObserver = true) {
        this._observer.enabled = enableObserver;
        this.setPropertyVars();
        this.render();
        this.generateStylesheet();
    }
}

document.addEventListener('DOMContentLoaded', () => {
  const circlePath = 'm2.05,0h156.8l6.15,22L0,17.6,2.05,0Z';
  // const circlePath = 'm4.14,0h316.45l12.41,34L0,27.2,4.14,0Z';
  const lineHighlight = new FumLineHighlight(
    'highlight',
    '--fum-underline-hightlight-',
    false,
    false,
    {
      duration: '0.3s',
      timing: 'ease-in-out'
    },
    circlePath
  );
  lineHighlight
  .sx({
    root: {
      color: 'inherit',
      _svg: {
        zIndex: -2
      },
      _path: {
        fill: 'var(--highlight)',
        strokeWidth: '0'
      }
    }
  })
  .useObserver({
    root: null,
    rootMargin: '0px',
    threshold: 0.1
  })
  .init();
});